import { TRPCClientError } from '@trpc/client'

/**
 * Why a sync request failed, as far as the phone can tell:
 * - `timeout`: no answer in time (K42)
 * - `no_session`: no session to send, the request never left (K44)
 * - `refresh_failed`: the server refused the token and the refresh failed on the
 *   network (K44)
 * - `rejected`: the auth server rejected the refresh; the session is gone (K44)
 * - `unauthorized`: the server refused a freshly refreshed token too
 * - `network`: the request didn't reach the server
 * - `server`: the server answered with an error
 * - `unknown`: none of the above (an error thrown by the app itself)
 */
export type SyncFailureReason =
  | 'timeout'
  | 'no_session'
  | 'refresh_failed'
  | 'rejected'
  | 'unauthorized'
  | 'network'
  | 'server'
  | 'unknown'

/** A sync request that failed for a reason the phone knows. */
export class SyncRequestError extends Error {
  constructor(
    readonly reason: SyncFailureReason,
    message: string
  ) {
    super(message)
    this.name = 'SyncRequestError'
  }
}

/**
 * Whether the user has to log in again: only when the auth server rejected the
 * refresh. A token refused again after a refresh (`unauthorized`) may be the API
 * failing to check it (it answers 401 then too), so it waits like any failure.
 */
export const sessionLost = (reason: SyncFailureReason | undefined) =>
  reason === 'rejected'

/**
 * Whether a collection's failure ends the round of syncs: a failure every
 * request would meet (no connection, no session, no answer), as opposed to one
 * of the collection's own (the server refusing its rows), after which the next
 * collections still sync.
 */
export const endsSyncRound = (reason: SyncFailureReason) =>
  reason !== 'server' && reason !== 'unknown'

export function syncFailureReason(error: unknown): SyncFailureReason {
  const cause = error instanceof TRPCClientError ? error.cause : error
  if (cause instanceof SyncRequestError) return cause.reason
  // A fetch given up on (fetchWithTimeout, AbortSignal.timeout)
  if (cause instanceof DOMException && cause.name === 'TimeoutError') {
    return 'timeout'
  }
  if (error instanceof TRPCClientError) {
    // tRPC's own error, or a response it couldn't read (a gateway's 504 page)
    const status =
      error.data?.httpStatus ??
      (error.meta?.response as Response | undefined)?.status
    if (status === 401) return 'unauthorized'
    if (status) return 'server'
    // No response at all: fetch itself failed
    return 'network'
  }
  if (error instanceof TypeError) return 'network'
  return 'unknown'
}
